import applyTimezone from 'helpers/applyTimezone'
import React, { Component } from 'react'
import FormControl from '@material-ui/core/FormControl'
import {
  MuiPickersUtilsProvider,
  KeyboardTimePicker,
} from '@material-ui/pickers'
import ClearIcon from '@material-ui/icons/Clear'
import DateFnsUtils from '@date-io/date-fns'
import moment from 'moment'
import isEqual from 'lodash/isEqual'
import { connect } from 'react-redux'
import cn from 'classnames'
import { getTimeFormatMaterial } from 'helpers/dates'
import ProfileHelper from 'helpers/profile'
import { ReactComponent as ClockIcon } from 'assets/img/ic_clock.svg'

class JobTimeInput extends Component {
  constructor(props) {
    super(props)

    this.state = {
      value: null,
      oldValue: null,
      evtTaskInputId: '',
      isUpdating: false,
    }
  }

  componentDidMount() {
    const {
      inputData: { value, evtInputId },
    } = this.props
    const formattedValue = value ? moment(value, 'HH:mm:ss') : null

    // Initialize state with values from props
    // Only set evtTaskInputId if evtInputId is provided (for existing inputs)
    this.setState({
      value: formattedValue,
      oldValue: formattedValue,
      evtTaskInputId: evtInputId || '',
    })
  }

  shouldComponentUpdate(nextProps) {
    const { inputInfo, inputData } = this.props
    const { inputInfo: nextInputInfo, inputData: nextInputData } = nextProps
    const { value, evtInputId } = nextInputData
    const formattedValue = value ? moment(value, 'HH:mm:ss') : null

    if (
      !isEqual(inputInfo, nextInputInfo) ||
      !isEqual(inputData, nextInputData)
    ) {
      // Only update evtTaskInputId if it's not already set (to avoid overwriting)
      this.setState(prevState => ({
        value: formattedValue,
        oldValue: formattedValue,
        evtTaskInputId: prevState.evtTaskInputId || evtInputId,
      }))

      return false
    }

    return true
  }

  componentWillUnmount() {
    if (this.props.isUnitBlockOpen) {
      this.onInputBlur()
    }
  }

  setResultId = ({ id }) => {
    this.setState({
      evtTaskInputId: id,
    })
  }

  onTimeChange = value => {
    this.setState({
      value: value ? moment(value) : null,
    })
  }

  onChooseTime = date => {
    const {
      inputInfo,
      inputData: { evtInputId },
      onJobTaskAutoSave,
    } = this.props
    const time = date ? moment(date) : date

    // Set both value and oldValue at the same time
    this.setState(
      {
        value: time,
        oldValue: time,
        isUpdating: true,
      },
      () => {
        // Use the current evtTaskInputId from state to ensure we have the latest value
        const currentEvtTaskInputId = this.state.evtTaskInputId

        onJobTaskAutoSave(
          {
            evtInputId: evtInputId || currentEvtTaskInputId,
            input: inputInfo.id,
            result: time ? time.format('HH:mm:ss') : time,
            setResultId: this.setResultId,
          },
          () => {
            this.setState({
              isUpdating: false,
            })
          },
        )
      },
    )
  }

  onInputFocus = e => {
    const { value } = e.target
    const {
      inputInfo: { id: inputId },
      onJobTaskAutoSave,
      site,
      licensee,
      inputData: { evtInputId },
    } = this.props

    if (!value) {
      const newValue = applyTimezone(
        moment(),
        site?.timezone,
        licensee?.timezone,
      )

      this.setState(
        {
          value: newValue,
          oldValue: newValue,
          isUpdating: true,
        },
        () => {
          const currentEvtTaskInputId = this.state.evtTaskInputId

          onJobTaskAutoSave(
            {
              evtInputId: evtInputId || currentEvtTaskInputId,
              result: newValue.format('HH:mm:ss'),
              input: inputId,
              setResultId: this.setResultId,
            },
            () => {
              this.setState({
                isUpdating: false,
              })
            },
          )
        },
      )
    }
  }

  onInputBlur = () => {
    const {
      inputInfo,
      inputData: { evtInputId },
      onJobTaskAutoSave,
    } = this.props
    const { value, oldValue } = this.state
    const isValueValid = moment(value).isValid()
    const formattedValue = isValueValid ? value.format('HH:mm:ss') : null
    const isSameTime = moment(value).isSame(moment(oldValue))

    if (!isSameTime && (isValueValid || (value === null && oldValue))) {
      // Set both isUpdating and oldValue at the same time to avoid race conditions
      this.setState(
        {
          isUpdating: true,
          oldValue: value,
        },
        () => {
          // Use the current evtTaskInputId from state to ensure we have the latest value
          const currentEvtTaskInputId = this.state.evtTaskInputId

          onJobTaskAutoSave(
            {
              evtInputId: evtInputId || currentEvtTaskInputId,
              result: formattedValue,
              input: inputInfo.id,
              setResultId: this.setResultId,
            },
            () => {
              this.setState({
                isUpdating: false,
              })
            },
          )
        },
      )
    }
  }

  render() {
    const {
      disabled,
      className,
      inputInfo: {
        title,
        required,
        parameters: { unchangeable_time, can_set_current_datetime },
        id: inputId,
      },
      licensee: { usaTimeFormat },
      inputData,
    } = this.props
    const { evtInputId } = inputData
    const { value, isUpdating } = this.state
    const locale = ProfileHelper.getCurrentLocale()
    const drawAsterisk = required ? '*' : ''
    const isDisabled =
      disabled || (value && unchangeable_time && evtInputId) || isUpdating

    return (
      <FormControl fullWidth className={className}>
        <span className="job-field-label">
          {title} {drawAsterisk}
        </span>
        <div className="job-field-wrapper time-picker">
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
            <KeyboardTimePicker
              key={`time-picker-${inputId}`}
              autoOk
              disabled={isDisabled}
              value={value}
              format={getTimeFormatMaterial(usaTimeFormat)}
              onAccept={this.onChooseTime}
              onChange={this.onTimeChange}
              className="job-field-input-box"
              mask={usaTimeFormat ? '__:__ _M' : '__:__'}
              keyboardIcon={<ClockIcon />}
              KeyboardButtonProps={{
                disabled: evtInputId && unchangeable_time,
              }}
              inputProps={{
                className: cn('job-field-input', 'js-enter-press-input'),
                onFocus: can_set_current_datetime && this.onInputFocus,
                onBlur: this.onInputBlur,
              }}
              ampm={usaTimeFormat}
            />
            {value && !isDisabled && (
              <ClearIcon
                className="job-field-icon job-field-clear-icon"
                onClick={e => {
                  e.preventDefault()
                  this.onChooseTime(null)
                }}
              />
            )}
          </MuiPickersUtilsProvider>
        </div>
      </FormControl>
    )
  }
}

export default connect(state => ({
  licensee: state.getIn(['profile', 'profile', 'licensee']),
  site: state.getIn(['profile', 'profile', 'site']),
}))(JobTimeInput)
